/** Strings of the shell (navigation, home, sessions list, settings) and of the "planned" markers. */
export const caShell = {
  nav: {
    label: 'Navegació',
    footer: 'Peu de pàgina',
    home: 'Inici',
    sessions: 'Sessions',
    library: 'Missions',
    newMission: 'Nova missió',
    catalog: 'Catàleg',
    servers: 'Servidors',
    panel: 'Panell de servidors',
    reports: 'Informes',
    changelog: 'Novetats',
    roadmap: 'Full de ruta',
    status: 'Estat',
    tests: 'Proves',
    admin: 'Administració',
    profile: 'Perfil',
    me: 'Jo',
    more: 'Més',
    settings: 'Configuració',
    signIn: 'Entra',
    signOut: 'Surt',
    direction: 'Direcció',
    designer: 'Dissenyador',
  },
  landing: {
    or: 'O entra directament amb:',
  },
  planned: {
    chip: 'Pendent',
    title: 'Encara per fer',
    intro: 'Dissenyat i pendent d’implementar. Es mostra perquè sàpigues què vindrà.',
    soon: 'Encara no funciona',
    waitsGame: 'Espera proves en joc',
    waitsPanel: 'Espera el panell',
    /** The chip's tooltip: the stage of the roadmap page (FULL_DE_RUTA.md) the feature is in. */
    expected: (stage: string) => `Previst: ${stage}`,
    /** The stage names of FULL_DE_RUTA.md, by the roadmap slot (R3 to R6) of a planned feature. */
    stages: {
      R3: 'La sessió i la direcció, des del mòbil',
      R4: 'Crear missions',
      R5: 'Campanyes',
      R6: 'A punt per a tothom',
    },
    /** «Què ve» (/que-ve): the registry of the chips, read as a page. */
    page: {
      title: 'Què ve',
      intro:
        'Tot el que està dissenyat i encara no funciona, ordenat per etapa del full de ruta. Les marques «Pendent» que veus a les pàgines surten d’aquesta llista.',
      empty: 'Ara mateix no hi ha res pendent que tingui marca.',
      count: (n: number) => (n === 1 ? '1 funció' : `${n} funcions`),
      /** What a screen reader hears before an entry's reason. */
      reasonLabel: 'Per què encara no funciona:',
      toRoadmap: 'Veure el full de ruta sencer',
    },
  },
  home: {
    now: 'Ara',
    live: 'En joc',
    nextSession: 'Propera sessió',
    noSession: 'Cap sessió programada.',
    noSessionHint: 'Genera una missió i crea-hi una sessió, o espera que algú en programi una.',
    noSessionHintPlayer: 'Quan algú en programi una, sortirà aquí.',
    openSession: 'Obre la sessió',
    join: 'Uneix-te',
    briefing: 'Briefing',
    // How long until a scheduled night, by the page's own clock.
    countdown: (ms: number) => {
      const minutes = Math.floor(ms / 60_000);
      if (minutes < 1) return 'A punt de començar';
      const days = Math.floor(minutes / 1440);
      if (days >= 2) return `Falten ${days} dies`;
      const hours = Math.floor(minutes / 60);
      if (hours >= 1) {
        const rest = minutes % 60;
        return `${hours === 1 ? 'Falta' : 'Falten'} ${hours} h${rest > 0 ? ` ${rest} min` : ''}`;
      }
      return minutes === 1 ? 'Falta 1 min' : `Falten ${minutes} min`;
    },
    campaigns: 'Campanyes actives',
    welcome: (name: string) => `Hola, ${name}`,
    figures: 'Les teves xifres',
    statPlayed: 'Sessions jugades',
    statHours: 'Hores aquest mes',
    // One decimal under ten hours, where it still says something; none above.
    hours: (h: number) =>
      h >= 10 || h === 0 ? `${Math.round(h)} h` : `${h.toFixed(1).replace('.', ',')} h`,
    statMissions: 'Missions',
    statServers: 'Servidors en marxa',
    later: 'Després',
    allSessions: 'Totes les sessions',
    toPlay: 'Missions per jugar',
    lastPlayed: 'Últimes partides',
    noPlayed: 'Encara no has jugat cap partida.',
    report: 'Informe',
    reportOf: (title: string) => `Informe de «${title}»`,
    news: 'Novetats',
    allNews: 'Totes les novetats',
    // The closing line when the home's list is cut short: how many of that version's changes it left out.
    moreNews: (n: number) => (n === 1 ? 'I 1 canvi més' : `I ${n} canvis més`),
    noMissions: 'Encara no hi ha cap missió.',
    noMissionsHint: 'Genera la primera des de «Nova missió», o demana-ho a qui les prepara.',
    noMissionsHintPlayer: 'Les prepara qui dirigeix el grup; quan n’hi hagi, les veuràs aquí.',
    allMissions: 'Totes les missions',
    newMission: 'Nova missió',
    undated: 'Sense data',
    onServer: (name: string) => `al servidor ${name}`,
    playersOnline: (n: number) => (n === 1 ? '1 jugador connectat' : `${n} jugadors connectats`),
    openMission: 'La missió',
  },
  sessionsPage: {
    title: 'Sessions',
    intro: 'Totes les sessions de les missions que pots veure: en joc, programades i jugades.',
    live: 'En joc',
    scheduled: 'Programades',
    ended: 'Jugades',
    empty: 'Cap sessió encara.',
    emptyHint: 'Una sessió es crea des de la pàgina d’una missió.',
    undated: 'Sense data',
    revision: (n: number) => `revisió ${n}`,
  },
  settings: {
    title: 'Configuració',
    account: 'Compte',
    displayName: 'Nom que veuran els altres',
    save: 'Desa',
    saved: 'Desat.',
    identities: 'Maneres d’entrar',
    session: 'Sessió',
    signOutHint: 'Tanca la sessió en aquest dispositiu.',
    password: 'Contrasenya',
    passwordIntro: 'Canvia-la aquí.',
    passwordIntroMore:
      'Si has entrat només amb Steam o Discord i no en tens cap, posa’n una i podràs entrar també amb usuari i contrasenya.',
    currentPassword: 'Contrasenya actual',
    newPassword: 'Contrasenya nova',
    passwordChanged: 'Contrasenya canviada.',
    passwordOnlyWarning:
      'Només pots entrar amb contrasenya. Si l’oblides, només un administrador te la pot canviar: enllaça Steam o Discord per tenir una segona porta.',
    recoveryHint:
      'Si l’oblides, un administrador te’n pot posar una de nova des de la pàgina d’administració.',
    devices: 'Dispositius amb sessió oberta',
    devicesIntro: 'Cada navegador on has entrat.',
    devicesIntroMore: 'Tanca els que no reconeguis; aquest és el que fas servir ara.',
    thisDevice: 'Aquest dispositiu',
    revoke: 'Tanca la sessió',
    lastSeen: (when: string) => `vist per últim cop ${when}`,
    signedIn: (when: string) => `entrada el ${when}`,
    address: (ip: string) => `IP ${ip}`,
    /** The kind of a device signed in, from its browser's agent (test card 151). */
    deviceKinds: { phone: 'Mòbil', tablet: 'Tauleta', computer: 'Ordinador' } as Record<
      'phone' | 'tablet' | 'computer',
      string
    >,
  },
  adminActions: {
    resetPassword: 'Nova contrasenya',
    resetPasswordPrompt: (handle: string) => ({
      title: `Contrasenya nova per a @${handle}`,
      body: 'Dóna-la-hi en persona: el compte no té cap altra manera de recuperar-la.',
      label: 'Contrasenya',
      hint: 'Deu caràcters com a mínim.',
    }),
    resetPasswordDone: (handle: string) => `Contrasenya de @${handle} canviada.`,
    anonymise: 'Anonimitza',
    anonymiseConfirm: (handle: string) => ({
      title: `Anonimitzar @${handle}?`,
      body: 'El compte queda com «Compte anonimitzat», sense identitats ni rols, desactivat i amagat, i les seves sessions obertes es tanquen. No es pot desfer.',
    }),
    anonymiseDone: (handle: string) => `@${handle} anonimitzat.`,
  },
  signInToSee: 'Entra per veure aquesta pàgina.',
};
